/**
 * Extract a user-facing error message from a backoffice JSON error body.
 *
 * Mirrors the precedence used by Unity's LexisNexisModel
 * (apps/unity/Assets/Scripts/Models/LexisNexisModel.cs):
 *   response.message → response.error → caller-supplied fallback.
 *
 * The backoffice returns either `{ message }` or `{ error }` depending on the
 * failure path (validation / banned / cooldown / generic). Surfacing whichever
 * is present preserves the operational nuance instead of collapsing every
 * failure into one generic string.
 */
export function pickApiErrorMessage(data: unknown, fallback: string): string {
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message.length > 0) return obj.message;
    if (typeof obj.error === 'string' && obj.error.length > 0) return obj.error;
  }
  return fallback;
}

/**
 * Read a fetch Response body as JSON, tolerating non-JSON / empty bodies.
 * Returns `null` if the body cannot be parsed — callers can then fall back
 * to a generic message, matching Unity's behavior when deserialization fails.
 */
export async function safeReadJson(res: Response): Promise<unknown | null> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Reduce a caught error to a structured, PII-safe shape for `console.error`.
 * Logging the raw Error object can leak response-body fragments, JWTs in
 * error messages, or stack frames containing player data into Sentry / RUM /
 * devtools. Surfacing just `{ message, status }` keeps the operational signal
 * without the player data.
 */
export function errorToLogObject(e: unknown): { message?: string; status?: number } {
  if (!e || typeof e !== 'object') {
    return { message: typeof e === 'string' ? e : undefined };
  }
  const err = e as { message?: unknown; status?: unknown };
  return {
    message: typeof err.message === 'string' ? err.message : undefined,
    status: typeof err.status === 'number' ? err.status : undefined
  };
}
